import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { colors, typography } from "../styles/designSystem";
import { SectionHeadline } from "./Typography";
import CartSenseShot from "../pngs/Prompt_Page.png";
import NeboShot from "../pngs/Nebo_App_Home.png";

const Section = styled.section`
    padding: 5rem 1.5rem;
    max-width: 1400px;
    margin: 0 auto;
    text-align: center;
`;

const Heading = styled(SectionHeadline)`
    text-align: center;
    margin-bottom: 1rem;
`;

const Subtext = styled.p`
    font-size: ${typography.fontSize[300]};
    line-height: 1.7;
    color: ${({ theme }) => theme.subtleText};
    max-width: 640px;
    margin: 0 auto 3rem;
`;

const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

const Card = styled(Link)`
    display: flex;
    flex-direction: column;
    background: ${({ theme }) => theme.cardBackground};
    border: 1px solid ${({ theme }) => theme.borderLight};
    overflow: hidden;
    text-align: left;
    color: inherit;
    box-shadow: inset 0 0 40px rgba(0, 0, 0, 0.5);
    transition: box-shadow 0.14s ease-out, border-color 0.14s ease-out;

    &:hover,
    &:visited {
        color: inherit;
    }

    &:hover {
        border-color: ${({ color }) => color};
        box-shadow: 0 0 24px ${({ theme }) => theme.glowSoft},
                    inset 0 0 40px rgba(0, 0, 0, 0.5);
    }
`;

const Thumb = styled.div`
    height: 200px;
    position: relative;
    background: ${({ color }) => color}12;
    border-bottom: 1px solid ${({ theme }) => theme.borderLight};

    /* Scanline the preview so it reads as a feed on the terminal, not a screenshot. */
    &::after {
        content: "";
        position: absolute;
        inset: 0;
        pointer-events: none;
        background: repeating-linear-gradient(
            to bottom,
            rgba(0, 0, 0, 0.35) 0px,
            rgba(0, 0, 0, 0.35) 1px,
            transparent 1px,
            transparent 3px
        );
    }
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: top center;
    }
`;

const Body = styled.div`
    padding: 2rem;
    display: flex;
    flex-direction: column;
    flex: 1;
`;

const Label = styled.span`
    align-self: flex-start;
    font-size: ${typography.fontSize[100]};
    font-weight: ${typography.fontWeight.semibold};
    font-family: ${typography.fontFamily.display};
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.widest};
    color: ${({ color }) => color};
    border: 1px solid ${({ color }) => color}55;
    padding: 3px 10px;
    margin-bottom: 1rem;
`;

const ProductTitle = styled.h3`
    font-family: ${typography.fontFamily.display};
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.wide};
    font-size: ${typography.fontSize[600]};
    font-weight: ${typography.fontWeight.bold};
    color: ${({ theme }) => theme.text};
    margin: 0 0 0.75rem;
`;

const Description = styled.p`
    font-size: ${typography.fontSize[300]};
    line-height: 1.8;
    color: ${({ theme }) => theme.subtleText};
    margin: 0 0 1.5rem;
`;

const TechRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: auto;
    margin-bottom: 1.5rem;
`;

const TechChip = styled.span`
    font-family: ${typography.fontFamily.display};
    font-size: ${typography.fontSize[100]};
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.wide};
    color: ${({ theme }) => theme.subtleText};
    background: transparent;
    border: 1px solid ${({ theme }) => theme.borderLight};
    padding: 0.25rem 0.6rem;
`;

// Matches the employment record's Access chip: inverts to the card's own accent on
// hover, so both "Access Record" affordances on the homepage behave identically.
const ViewLink = styled.span`
    display: inline-block;
    align-self: flex-start;
    font-family: ${typography.fontFamily.display};
    font-size: ${typography.fontSize[150]};
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.wider};
    color: ${({ color }) => color};
    padding: 0.25rem calc(0.5rem - ${typography.letterSpacing.wider}) 0.25rem 0.5rem;
    border: 1px solid transparent;
    transition: background 0.14s ease-out, color 0.14s ease-out,
                border-color 0.14s ease-out, box-shadow 0.14s ease-out;

    &::before {
        content: "[";
        opacity: 0.5;
        margin-right: 0.3rem;
    }

    &::after {
        content: "]";
        opacity: 0.5;
        margin-left: 0.3rem;
    }

    ${Card}:hover & {
        background: ${({ color }) => color};
        border-color: ${({ color }) => color};
        color: ${({ theme }) => theme.background};
        text-shadow: none;
        box-shadow: 0 0 14px ${({ color }) => color};
    }
`;

const products = [
    {
        to: "/portfolio/cartsense",
        image: CartSenseShot,
        label: "Record 01 // Shipped",
        title: "CartSense",
        color: colors.emerald[500],
        description:
            "An AI meal-planning platform I built solo, end to end. A Next.js and TypeScript front end over a Firebase backend, with an OpenAI generation pipeline wired into authentication, subscriptions, onboarding, and grocery checkout flows.",
        tech: ["Next.js", "TypeScript", "Firebase", "OpenAI"],
    },
    {
        to: "/portfolio/nebo",
        image: NeboShot,
        label: "Record 02 // Founder",
        title: "Nebo",
        color: colors.cyan[300],
        description:
            "An asset registration and recovery platform owned end to end. A React Native app over a Supabase and PostgreSQL backend with row-level security, plus a recovery engine that scores marketplace listing photos against registered items through a multi-stage matching pipeline.",
        tech: ["React Native", "Supabase", "Node.js", "Claude Vision"],
    },
];

const ProductsSection = () => {
    return (
        <Section>
            <Heading>Personal Projects</Heading>
            <Subtext>
                Products I built and shipped on my own — the full path from idea to production,
                across product, engineering, and infrastructure.
            </Subtext>
            <Grid>
                {products.map((product) => (
                    <Card key={product.title} to={product.to} color={product.color}>
                        <Thumb color={product.color}>
                            <img src={product.image} alt={`${product.title} preview`} />
                        </Thumb>
                        <Body>
                            <Label color={product.color}>{product.label}</Label>
                            <ProductTitle>{product.title}</ProductTitle>
                            <Description>{product.description}</Description>
                            <TechRow>
                                {product.tech.map((t) => (
                                    <TechChip key={t}>{t}</TechChip>
                                ))}
                            </TechRow>
                            <ViewLink color={product.color}>Access Record</ViewLink>
                        </Body>
                    </Card>
                ))}
            </Grid>
        </Section>
    );
};

export default ProductsSection;
